import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import StyledCollectionEntitiesTable
  from 'components/organisms/Tables/CollectionEntitiesTable/CollectionEntitiesTable.styles';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import utils from 'helpers/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import constants from 'helpers/constants';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import ButtonGroupTableCell from 'components/molecules/TableCells/ButtonGroupTableCell/ButtonGroupTableCell';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';

const AddRejectActionCell = React.forwardRef(({cell, collection, isSuggestion,
                                                entityAccept, entityUnAssign,
                                                snackbar, dialogControl}, ref) => {
  const entity = cell.row.original;

  const actions = useMemo(() => {
    return [{
      auth: utils.createAuth({attribute: 'collection.update', meta: {collection}}),
      label: isSuggestion ? 'Remove suggestion' : 'Remove from collection',
      tooltip: isSuggestion ? 'Remove suggestion' : 'Remove from collection',
      icon: Delete,
      color: 'error',
      onClick: (e) => {
        entityUnAssign({entityId: entity.entityId})
          .catch(() => {
            snackbar.show(`Removing ${isSuggestion ? 'suggestion' : 'company'} from collection '${collection.name}' failed`, null,
              {color: 'error', autoHideDuration: constants.delay.error});
          });

        e.preventDefault();
      }
    }, {
      auth: utils.createAuth({attribute: 'collection.update', meta: {collection}}),
      label: 'Add to collection',
      tooltip: 'Add to collection',
      icon: Add,
      color: 'primary',
      onClick: (e) => {
        entityAccept({entityId: entity.entityId})
          .catch(() => {
            snackbar.show(`Adding ${isSuggestion ? 'suggestion' : 'company'} to collection '${collection.name}' failed`, null,
              {color: 'error', autoHideDuration: constants.delay.error});
          });
        e.preventDefault();
      }
    }];
  }, [collection, isSuggestion, entityUnAssign, entity.entityId, snackbar, entityAccept]);

  const ActionButtonGroupProps = useMemo(() => ({
    ActionButtonComponent: ActionIconButton,
    ActionButtonProps: {
      showInactive: true,
      variant: 'outlined',
      size: 'smaller',
      density: 'sparse',
      IconProps: {
        size: 'smaller'
      }
    },
    radius: 'round',
    variant: 'outlined',
    actions
  }), [actions]);

  return <ButtonGroupTableCell ref={ref}
                               ActionButtonGroupProps={ActionButtonGroupProps}/>
});

const CollectionEntitiesTable = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'CollectionEntitiesTable');

  const tableProvider = useTable();
  const isSuggestion = Boolean(tableProvider?.view?.activeViews?.find((v) => v.name === 'suggestions'));
  const collection = tableProvider.context?.data;
  const columnDefinitions = tableProvider.columnDefinitions;

  const authorize = useAuthorize();

  const entityUnAssignEvent = useEffectEvent(tableProvider.updaters?.unAssignItems);
  const entityAcceptEvent = useEffectEvent(tableProvider.updaters?.acceptItems);

  const handleCanUpdate = useCallback((entity, column) => {
    return authorize({attribute: 'collection.entity.update', meta: {collection, entity}}) &&
      authorize(column.auth?.update ? {...column.auth?.update, meta: {
          ...column.auth?.update?.meta,
          collection,
          entity
        }} : {})
  }, [collection, authorize]);

  const clearSelectionEvent = useEffectEvent(tableProvider.listSelection?.clearSelection);
  const columns = useMemo(() => {
    const columns = [];
    if (columnDefinitions) {
      columnDefinitions
        .forEach((columnDef) => {
          if (columnDef.name === 'addRejectCollection') {
            columns.push({
              accessorKey: utils.camelcase(columnDef.name),
              id: columnDef.id,
              header: columnDef.header,
              size: 100,
              minSize: 100,
              enableSorting: columnDef.sortable !== false,
              enableEditing: false,
              Cell: (props) => <AddRejectActionCell {...props}
                                                    collection={collection}
                                                    isSuggestion={isSuggestion}
                                                    entityAccept={entityAcceptEvent}
                                                    entityUnAssign={entityUnAssignEvent}
                                                    clearSelection={clearSelectionEvent}/>
            })
          }
        });
    }

    return columns;
  }, [isSuggestion, columnDefinitions, collection, clearSelectionEvent, entityUnAssignEvent, entityAcceptEvent]);


  return <StyledCollectionEntitiesTable ref={ref} {...innerProps}
                                        columns={columns}
                                        onCanUpdate={handleCanUpdate}/>
});

CollectionEntitiesTable.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default CollectionEntitiesTable;
